<template>
  <div>
    <!-- Start: Header -->
    <section class="section-header-info">
      <div class="container">
        <div class="row">
          <div class="col-sm-12">
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item">
                  <a href="javascript:void(0)">Finance</a>
                </li>
                <li class="breadcrumb-item active" aria-current="page">Summary</li>
              </ol>
            </nav>
            <div class="content-header-info">
              <div class="col-sm-12">
                <div class="pull-left">
                  <h2>
                    Finance <span><i class="ic-corporate-card"></i></span>
                  </h2>
                  <p>Manage your corporate fund deposits and refunds.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- End: Header -->

    <!-- Start: Table -->
    <section class="middle-content">
      <div class="container">
        <div class="row">
          <div class="col-sm-6">
            <div class="dataTables_filter">
              <i class="ic-search"></i>
              <input
                type="search"
                class="form-control"
                v-model="table.keyword"
                @keyup="onKeyupKeyword()"
                placeholder="Search"
              />
            </div>
          </div>
          <div class="col-sm-6">
            <div class="dataTables_length pull-right">
              <label>
                <select
                  class="form-control v-select"
                  v-model="table.pageSize"
                  @change="onChangePageSize()"
                >
                  <option v-for="item in table.pageSizes" :value="item" :key="item">
                    {{ item }}
                  </option>
                </select>
                Entries
              </label>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="table-responsive table-accordion table-p15">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Company</th>
                  <th class="text-right">Threshold Amount</th>
                  <th class="text-right">Current Balance</th>
                  <th class="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr class="detail-row" v-for="item in items" :key="item.id">
                  <td>{{ item.companyName }}</td>
                  <td class="pos-rel text-right">
                    <span :class="{ 'sts-danger': item.minimumBalance === 0 }">{{
                      printThresholdBalance(item)
                    }}</span>
                    <div
                      v-if="isAllowToSetThreshold || isAllowToSuspend"
                      class="row-big-menu"
                    >
                      <ul>
                        <li v-if="isAllowToSetThreshold">
                          <button
                            class="btn btn-default"
                            type="button"
                            @click.prevent="openModal(item)"
                          >
                            <span>Threshold</span>
                          </button>
                        </li>
                        <li v-if="isAllowToSuspend">
                          <button
                            class="btn btn-default sts-danger"
                            :class="{
                              'sts-danger': item.isActive,
                              '--green35': !item.isActive,
                            }"
                            type="button"
                            @click.prevent="openSuspendDialog(item)"
                          >
                            <span> {{ item.isActive ? "Suspend" : "Activate" }}</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </td>
                  <td class="text-right">
                    {{ item.moneyFormat.currencyCode }}
                    {{ printMoney(item.currentBalance, item.moneyFormat) }}
                  </td>
                  <td
                    class="text-center"
                    :class="{ '--green35': item.isActive, 'sts-danger': !item.isActive }"
                  >
                    {{ item.isActive ? "Active" : "Suspended" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-5">
            <div class="dataTables_info --blue25">
              <strong>{{ recordInfo }}</strong>
            </div>
          </div>
          <div class="col-sm-7">
            <nav aria-label="...">
              <paginate
                v-model="table.pageNumber"
                :page-count="table.totalPages"
                :click-handler="onNavigateCallback"
                :prev-text="'Previous'"
                :next-text="'Next'"
                :container-class="'pagination pull-right'"
              >
              </paginate>
            </nav>
          </div>
        </div>
      </div>
    </section>
    <!-- End: Table -->

    <!-- Start: Modal -->
    <div class="modal fade" role="dialog" ref="vuemodal">
      <div class="modal-dialog modal-xs">
        <!-- Modal content-->
        <div class="modal-content">
          <div class="modal-header">
            <div class="row">
              <div class="col-sm-10">
                <h4>Set Threshold Amount</h4>
                <h6>Please fill all information below.</h6>
              </div>
              <div class="col-sm-2">
                <button
                  type="button"
                  class="close"
                  data-dismiss="modal"
                  aria-label="Close"
                >
                  <i class="ic-close"></i>
                </button>
              </div>
            </div>
          </div>
          <!-- /.modal-header -->
          <div class="modal-body">
            <div class="modal-wrapper">
              <div class="col-sm-12">
                <div class="modal-content-body">
                  <div class="summary-error" v-if="$v.model.$error">
                    This form has errors
                  </div>
                  <form @submit.prevent="submitMinimumBalance">
                    <div class="form-group" v-if="model.moneyFormat">
                      <label class="required">
                        Amount
                        <span class="--grey50"
                          >({{ model.moneyFormat.currencyCode }})</span
                        >
                      </label>
                      <currency-input
                        class="form-control"
                        :class="{
                          'field-error': isSubmitted && $v.model.minimumBalance.$error,
                        }"
                        autocomplete="off"
                        placeholder="Enter rate"
                        v-model="model.minimumBalance"
                        :allow-negative="false"
                        :currency="null"
                        :locale="model.moneyFormat.cultureCode"
                        :precision="model.moneyFormat.numberOfDecimal"
                      />
                      <div v-if="isSubmitted && $v.model.minimumBalance.$error">
                        <small
                          class="form-text sts-danger"
                          v-if="!$v.model.minimumBalance.required"
                          >Min Balance is required</small
                        >
                        <small
                          class="form-text sts-danger"
                          v-if="!$v.model.minimumBalance.minValue"
                          >Min Balance is
                          {{ $v.model.minimumBalance.$params.minValue.min }}</small
                        >
                      </div>
                    </div>
                    <div class="modal-footer text-right">
                      <button type="submit" class="btn btn-primary">Save</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <!-- /.modal-wrapper -->
          </div>
        </div>
      </div>
    </div>

    <confirmModal
      :id="'SuspendAccountConfirmation'"
      :header="'Suspend'"
      :content="'Are you sure to suspend this account?'"
      :cancel-label="'Cancel'"
      :submit-label="'Yes, Suspend'"
      :color="'delete-icon'"
      :icon="'ic-close'"
      @submit="suspendAccount"
    />
    <confirmModal
      :id="'ActivateAccountConfirmation'"
      :header="'Activate'"
      :content="'Are you sure to activate this account?'"
      :cancel-label="'Cancel'"
      :submit-label="'Yes, Activate'"
      :color="'confirm-icon'"
      :icon="'ic-check'"
      @submit="suspendAccount"
    />
    <!-- End: Modal -->

    <!-- Start: BlockUI -->
    <loader :content="msg" :visible="isLoading" />
    <!-- End: BlockUI -->
  </div>
</template>

<script>
import { required, minValue } from "vuelidate/lib/validators";
import numeral from "numeral";
import confirmModal from "@/components/shared/ConfirmModal";
import deleteModal from "@/components/shared/DeleteModal";
import depositService from "@/services/master/deposit/depositSummaryService.js";
import companyService from "@/services/master/company/companyService.js";

export default {
  components: {
    confirmModal,
    deleteModal,
  },
  computed: {
    isAllowToRead: function () {
      return this.allPermissions.includes("Read");
    },
    isAllowToSetThreshold: function () {
      return this.allPermissions.includes("ManageThreshold");
    },
    isAllowToSuspend: function () {
      return this.allPermissions.includes("Suspend");
    },
    recordInfo: function () {
      return this.table.totalRecords > 1
        ? `${this.table.totalRecords} Records`
        : `${this.table.totalRecords} Record`;
    },
  },
  data() {
    return {
      table: {
        keyword: "",
        totalPages: 1,
        totalRecords: 0,
        pageNumber: 1,
        pageSize: 25,
        pageSizes: [25, 50, 100],
      },
      isSubmitted: false,
      allPermissions: [],
      itemToBeDeleted: null,
      accountToBeSuspendedOrActivated: null,
      items: [],
      model: {},
      isLoading: false,
      msg: "Please wait",
    };
  },
  validations() {
    return {
      model: {
        minimumBalance: { required, minValue: minValue(0) },
      },
    };
  },
  async created() {
    this.$helper.scrollTop();
    const publicSettings = await this.$store.dispatch("setting/getAllPublic");
    this.table.pageSize = this.$helper.extractSetting(publicSettings, "PageSize", 25);

    const profile = await this.$store.dispatch("user/getProfile");
    const permissions = profile.services[this.$route.name];
    if (permissions) {
      this.allPermissions = permissions;
    }

    if (this.isAllowToRead) {
      await this.getAll();
    }
  },
  updated() {
    this.$helper.collapseMenu("deposit");
    this.$helper.setNavigationClass("deposit-link");
  },
  methods: {
    async onChangePageSize() {
      this.table.pageNumber = 1;
      await this.getAll();
    },
    async onKeyupKeyword() {
      const self = this;
      if (self.timer) {
        clearTimeout(self.timer);
        self.timer = null;
      }
      self.timer = setTimeout(async (_) => {
        self.table.pageNumber = 1;
        await self.getAll();
      }, 500);
    },
    async onNavigateCallback(pageNumber) {
      this.table.pageNumber = pageNumber;
      await this.getAll();
    },
    resetTable() {
      this.table.keyword = "";
      this.table.pageNumber = 1;
      this.table.pageSize = 5;
    },
    resetModel() {
      this.model = {};
    },
    resetValidations() {
      this.isSubmitted = false;
      this.$v.model.$reset();
    },
    openModal(item) {
      this.resetModel();
      this.resetValidations();
      this.model = Object.assign({}, item);
      $(this.$refs.vuemodal).modal({ backdrop: "static", keyboard: false });
    },
    closeModal() {
      this.resetModel();
      this.resetValidations();
      $(this.$refs.vuemodal).modal("hide");
    },
    async getAll() {
      if (!this.isAllowToRead) {
        return;
      }
      try {
        this.isLoading = true;
        let filter = `?page=${this.table.pageNumber}&size=${this.table.pageSize}`;
        if (this.table.keyword !== null && this.table.keyword !== "") {
          filter += `&search=${this.table.keyword}`;
        }
        const response = await depositService.getAll(filter);
        this.items = response.data;
        this.table.totalPages = response.totalPages;
        this.table.totalRecords = response.totalRecords;
      } catch (error) {
        if (error.errors) {
          for (let index = 0; index < error.errors.length; index++) {
            this.$notification.error(error.errors[index], { title: "Error" });
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    async submitMinimumBalance() {
      this.isSubmitted = true;
      this.$v.model.$touch();
      if (this.$v.model.$error) {
        return;
      }
      try {
        this.isLoading = true;
        this.model.minimumBalance = parseFloat(this.model.minimumBalance);
        const response = await depositService.setMinimumBalance(this.model);
        this.$notification.success("Successfully updated.", { title: "Success" });
        await this.getAll();
        this.closeModal();
        this.resetTable();
      } catch (error) {
        if (error.errors) {
          for (let index = 0; index < error.errors.length; index++) {
            this.$notification.error(error.errors[index], { title: "Error" });
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    openSuspendDialog(item) {
      this.accountToBeSuspendedOrActivated = item;
      if (item.isActive) {
        $("#SuspendAccountConfirmation").modal({ backdrop: "static", keyboard: false });
      } else {
        $("#ActivateAccountConfirmation").modal({ backdrop: "static", keyboard: false });
      }
    },
    async suspendAccount() {
      try {
        this.isLoading = true;
        await depositService.suspend({
          id: this.accountToBeSuspendedOrActivated.id,
          isActive: this.accountToBeSuspendedOrActivated.isActive,
        });
        this.$notification.success("Successfully suspended.", { title: "Success" });
        await this.getAll();
      } catch (error) {
        if (error.errors) {
          for (let i = 0; i < error.errors.length; i++) {
            this.$notification.error(error.errors[i], { title: "Error" });
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    printMoney(value, moneyFormat) {
      return numeral(value).format(moneyFormat.decimalFormat);
    },
    printThresholdBalance(item) {
      return item.minimumBalance > 0
        ? `${item.moneyFormat.currencyCode} ${this.printMoney(
            item.minimumBalance,
            item.moneyFormat
          )}`
        : "N/A";
    },
  },
};
</script>

<style scoped>
.detail-row td {
  height: 55px;
}
</style>



// WEBPACK FOOTER //
// src/components/deposit/summary/DepositSummary.vue